import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { FunnelIcon } from '@heroicons/react/24/outline'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import MonitorCard from 'components/Dashboard/MonitorCard'
import StatCards from 'components/Dashboard/StatCards'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { parseDate } from 'utils'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'

// GET request for daily agro summary data
const getDailyAgroSummaryData = async (farmId, dateRange) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/agro-summary?${buildDashboardDateRangeQuery(dateRange)}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching daily agro summary data:', error)
    throw error
  }
}

const AgroActionsTable = ({ agroActionsSummary }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'blockName', labelKey: 'columns.blockName' },
      { key: 'crop', labelKey: 'columns.crop' },
      { key: 'variety', labelKey: 'columns.variety' },
      { key: 'activityName', labelKey: 'columns.activityName' },
      { key: 'units', labelKey: 'columns.units' },
      { key: 'percentageOfCompletion', labelKey: 'columns.percentageOfCompletion', cell: (value) => `${value}%` }
    ],
    rows: agroActionsSummary
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersMonitorTable = ({ workersMonitor }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      // Workers are still identified by their legacy id (old_id); fall back to
      // the internal worker_id only when a worker has no legacy id.
      { key: 'workerId', labelKey: 'columns.workerId', cell: (value, row) => row.oldId || row.workerId },
      { key: 'workerName', labelKey: 'columns.workerName' },
      { key: 'activity', labelKey: 'columns.activity' },
      { key: 'blockName', labelKey: 'columns.blockName' },
      { key: 'totalPlants', labelKey: 'columns.units' },
      { key: 'totalHours', labelKey: 'columns.totalWorkHours' },
      { key: 'plantsPerHour', labelKey: 'columns.averageRatePerHour' },
      { key: 'groupName', labelKey: 'columns.groupName' },
      { key: 'firstScan', labelKey: 'columns.firstScan', cell: (value) => parseDate(value) },
      { key: 'lastScan', labelKey: 'columns.lastScan', cell: (value) => parseDate(value) }
    ],
    rows: workersMonitor
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const DailyAgroSummary = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [agroSummaryData, setAgroSummaryData] = useState({
    dailyAgroSummary: {
      totalActions: 0,
      workerCount: 0,
      averagePerWorker: 0
    },
    agroActionsSummary: [],
    workersMonitor: [],
    loading: true,
    error: null
  })

  // Get current date in Mexico City timezone
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)

  const fetchAgroSummaryData = async (rangeToFetch = dateRange) => {
    if (!user?.farmId) return

    try {
      setAgroSummaryData(prev => ({ ...prev, loading: true, error: null }))

      const data = await getDailyAgroSummaryData(user.farmId, rangeToFetch)

      setAgroSummaryData(prev => ({
        ...prev,
        dailyAgroSummary: data.dailyAgroSummary || prev.dailyAgroSummary,
        agroActionsSummary: data.agroActionsSummary || [],
        workersMonitor: data.workersMonitor || [],
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching agro summary:', error)
      setAgroSummaryData(prev => ({
        ...prev,
        loading: false,
        error: t('dashboard.agro.error')
      }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    fetchAgroSummaryData(dateRange)
  }, [user?.farmId])

  useEffect(() => {
    if (!hasFetched.current) return
    fetchAgroSummaryData(dateRange)
  }, [dateRange])

  const cardData = [
    {
      title: t('dashboard.agro.totalActions'),
      value: agroSummaryData.dailyAgroSummary.totalActions,
      unit: t('columns.units'),
      color: 'bg-red-700'
    },
    {
      title: t('dashboard.agro.costPerUnit'),
      value: '$0.01',
      unit: t('dashboard.agro.perUnit'),
      color: 'bg-red-500'
    },
    {
      title: t('dashboard.agro.workerCount'),
      value: agroSummaryData.dailyAgroSummary.workerCount,
      unit: t('common.workers'),
      color: 'bg-red-600'
    },
    {
      title: t('dashboard.agro.avgPerWorker'),
      value: agroSummaryData.dailyAgroSummary.averagePerWorker,
      unit: t('columns.units'),
      color: 'bg-red-400'
    }
  ]

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-red-600'>{t('dashboard.agro.title')}</h1>
          <p className='text-sm text-gray-600'>
            {t('dashboard.agro.agroRange', { range: getDateRangeSummary(dateRange) })}{' '}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>{t('dashboard.advancedFilters')}</span>
          </ButtonComponent>
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      {agroSummaryData.loading && <PageLoadingState message={t('dashboard.agro.loading')} rows={12} />}

      {!agroSummaryData.loading && agroSummaryData.error && (
        <div className='text-red-500'>{agroSummaryData.error}</div>
      )}

      {!agroSummaryData.loading && !agroSummaryData.error && (
        <>
          <StatCards cards={cardData} />

          <MonitorCard title={t('dashboard.agro.agroActionsSummary')} date={getDateRangeSummary(dateRange)} hasData={agroSummaryData.agroActionsSummary.length > 0} color='red'>
            <AgroActionsTable agroActionsSummary={agroSummaryData.agroActionsSummary} />
          </MonitorCard>

          <MonitorCard title={t('dashboard.agro.workersMonitor')} date={getDateRangeSummary(dateRange)} hasData={agroSummaryData.workersMonitor.length > 0} color='red'>
            <WorkersMonitorTable workersMonitor={agroSummaryData.workersMonitor} />
          </MonitorCard>
        </>
      )}
      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        farmingBlocks={filterOptions.farmingBlocks}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={currentDate}
        onClose={() => setShowDateRangeModal(false)}
        onApply={(nextRange) => {
          setDateRange(nextRange)
          setShowDateRangeModal(false)
        }}
      />
    </div>
  )
}

export default DailyAgroSummary
